/**
 * SectionBody — the master switch that renders each CMS editor section.
 * Extracted from CMSEditor.jsx so the container stays focused on data flow.
 * Every case block receives (draft, set, setArr) so it can read and mutate
 * the in-memory CMS draft.
 */
import React from "react";
import { Link } from "react-router-dom";
import api from "@/api/client";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ExternalLink, Trash2, Circle, MessageSquare, ChevronDown,
  Facebook, Twitter, Instagram, Linkedin, Youtube, Send, Share2,
} from "lucide-react";
import MediaUploader from "@/components/MediaUploader";
import {
  Grid2, SubTitle, Hint, Code, Field, FieldFull, ColorField, ThemePreset,
  ThemePalettePreview, LoginThemePreview, ArrayEditor,
} from "./primitives";
import { SEOFields } from "./SEOFields";
import { LiveWidgetSection } from "./LiveWidgetSection";
import { DangerZoneCard } from "./DangerZoneCard";

export function SectionBody({ id, draft, set, setArr }) {
  switch (id) {
    /* ═════ BRAND TAB ═════ */
    case "brand-identity":
      return (
        <>
          <Grid2>
            <Field label="Company name (default)"><Input value={draft.brand?.name || ""} onChange={(e) => set("brand.name", e.target.value)} /></Field>
            <Field label="Tagline (public landing)"><Input value={draft.brand?.tagline || ""} onChange={(e) => set("brand.tagline", e.target.value)} /></Field>
          </Grid2>
          <div>
            <SubTitle>Master logo</SubTitle>
            <Hint>Fallback used everywhere unless a per-panel logo is set. Appears in the public header, all admin/customer/driver navbars, on the login screen, and on PDF invoices. Recommended: square or wide transparent PNG/SVG, 512×512 min.</Hint>
            <MediaUploader
              value={draft.brand?.logo_url || ""}
              onChange={(url) => set("brand.logo_url", url)}
              accept="image/*"
              maxSizeMb={4}
              placeholder="Upload logo (PNG / SVG / JPG)"
              testId="upload-brand-logo"
            />
          </div>
        </>
      );

    case "brand-customer":
      return (
        <>
          <Hint>Shown at the top-left of every logged-in customer page (<Code>/portal/*</Code>). Leave blank to reuse the master brand.</Hint>
          <Grid2>
            <Field label="Display name"><Input value={draft.brand?.customer_panel_name || ""} placeholder={draft.brand?.name || "FuelExpress"} onChange={(e) => set("brand.customer_panel_name", e.target.value)} data-testid="cust-panel-name" /></Field>
            <Field label="Tagline / sub-line"><Input value={draft.brand?.customer_panel_tagline || ""} placeholder="Customer Portal" onChange={(e) => set("brand.customer_panel_tagline", e.target.value)} data-testid="cust-panel-tag" /></Field>
          </Grid2>
          <div>
            <SubTitle>Alternate logo (optional)</SubTitle>
            <MediaUploader
              value={draft.brand?.customer_panel_logo_url || ""}
              onChange={(url) => set("brand.customer_panel_logo_url", url)}
              accept="image/*"
              maxSizeMb={4}
              placeholder="Upload distinct customer-portal logo"
              testId="upload-cust-panel-logo"
            />
          </div>
        </>
      );

    case "brand-admin":
      return (
        <>
          <Hint>Shown at the top-left of the admin ERP (<Code>/app/*</Code>) — administrators, dispatchers, accounts, operators. Leave blank to reuse the master brand.</Hint>
          <Grid2>
            <Field label="Display name"><Input value={draft.brand?.admin_panel_name || ""} placeholder={draft.brand?.name || "FuelExpress"} onChange={(e) => set("brand.admin_panel_name", e.target.value)} data-testid="admin-panel-name" /></Field>
            <Field label="Tagline / sub-line"><Input value={draft.brand?.admin_panel_tagline || ""} placeholder="Operations ERP" onChange={(e) => set("brand.admin_panel_tagline", e.target.value)} data-testid="admin-panel-tag" /></Field>
          </Grid2>
          <div>
            <SubTitle>Alternate logo (optional)</SubTitle>
            <MediaUploader
              value={draft.brand?.admin_panel_logo_url || ""}
              onChange={(url) => set("brand.admin_panel_logo_url", url)}
              accept="image/*"
              maxSizeMb={4}
              placeholder="Upload distinct admin-console logo"
              testId="upload-admin-panel-logo"
            />
          </div>
        </>
      );

    case "brand-driver":
      return (
        <>
          <Hint>Shown at the top-left of the driver app. Leave blank to reuse the master brand.</Hint>
          <Grid2>
            <Field label="Display name"><Input value={draft.brand?.driver_panel_name || ""} placeholder={draft.brand?.name || "FuelExpress"} onChange={(e) => set("brand.driver_panel_name", e.target.value)} data-testid="driver-panel-name" /></Field>
            <Field label="Tagline / sub-line"><Input value={draft.brand?.driver_panel_tagline || ""} placeholder="Driver App" onChange={(e) => set("brand.driver_panel_tagline", e.target.value)} data-testid="driver-panel-tag" /></Field>
          </Grid2>
          <div>
            <SubTitle>Alternate logo (optional)</SubTitle>
            <MediaUploader
              value={draft.brand?.driver_panel_logo_url || ""}
              onChange={(url) => set("brand.driver_panel_logo_url", url)}
              accept="image/*"
              maxSizeMb={4}
              placeholder="Upload distinct driver-app logo"
              testId="upload-driver-panel-logo"
            />
          </div>
        </>
      );

    case "brand-taglines":
      return (
        <Grid2>
          <Field label="Customer login sub-line"><Input value={draft.brand?.customer_login_tagline || ""} placeholder="Doorstep diesel delivery" onChange={(e) => set("brand.customer_login_tagline", e.target.value)} data-testid="cust-login-tag" /></Field>
          <Field label="Staff login sub-line"><Input value={draft.brand?.staff_login_tagline || ""} placeholder="Operations command centre" onChange={(e) => set("brand.staff_login_tagline", e.target.value)} data-testid="staff-login-tag" /></Field>
        </Grid2>
      );

    /* ═════ THEME TAB ═════ */
    case "theme-public":
      return (
        <>
          <Hint>Applies to the public landing page instantly on save. Use hex codes.</Hint>
          <Grid2>
            <ColorField label="Background" path="theme.background" draft={draft} set={set} />
            <ColorField label="Primary (CTA / accents)" path="theme.primary" draft={draft} set={set} />
            <ColorField label="Primary hover" path="theme.primary_hover" draft={draft} set={set} />
            <ColorField label="Surface (cards)" path="theme.surface" draft={draft} set={set} />
            <ColorField label="Surface alt (hero halo)" path="theme.surface_alt" draft={draft} set={set} />
            <ColorField label="Text (headings)" path="theme.text" draft={draft} set={set} />
            <ColorField label="Text muted" path="theme.text_muted" draft={draft} set={set} />
            <ColorField label="Border" path="theme.border" draft={draft} set={set} />
            <ColorField label="Dark (footer / app section)" path="theme.dark" draft={draft} set={set} />
          </Grid2>
          <div>
            <SubTitle>Quick presets</SubTitle>
            <div className="flex flex-wrap gap-2">
              <ThemePreset label="FuelBuddy light (default)" onClick={() => set("theme", { mode:"light", background:"#FFFFFF", surface:"#F8FAFC", surface_alt:"#FFF6EF", primary:"#F26722", primary_hover:"#D9541A", text:"#0B2447", text_muted:"#64748B", border:"#E2E8F0", dark:"#0B2447", success:"#10B981" })} />
              <ThemePreset label="Ocean light" onClick={() => set("theme", { mode:"light", background:"#FFFFFF", surface:"#F0F9FF", surface_alt:"#E0F2FE", primary:"#0EA5E9", primary_hover:"#0284C7", text:"#0C4A6E", text_muted:"#64748B", border:"#E2E8F0", dark:"#0C4A6E", success:"#10B981" })} />
              <ThemePreset label="Forest light" onClick={() => set("theme", { mode:"light", background:"#FFFFFF", surface:"#F0FDF4", surface_alt:"#DCFCE7", primary:"#16A34A", primary_hover:"#15803D", text:"#14532D", text_muted:"#64748B", border:"#E2E8F0", dark:"#14532D", success:"#10B981" })} />
              <ThemePreset label="Cool teal" onClick={() => set("theme", { mode:"light", background:"#FFFFFF", surface:"#F0FDFA", surface_alt:"#CCFBF1", primary:"#14B8A6", primary_hover:"#0F766E", text:"#0F766E", text_muted:"#64748B", border:"#CCFBF1", dark:"#134E4A", success:"#10B981" })} />
              <ThemePreset label="Amber steel (dark)" onClick={() => set("theme", { mode:"dark", background:"#0A0E1A", surface:"#111827", surface_alt:"#1F2937", primary:"#F59E0B", primary_hover:"#D97706", text:"#FFFFFF", text_muted:"#94A3B8", border:"#1F2937", dark:"#000000", success:"#10B981" })} />
            </div>
          </div>
          <ThemePalettePreview theme={draft.theme || {}} />
        </>
      );

    case "theme-customer":
      return (
        <>
          <Hint>Controls the customer sign-in (<Code>/login</Code>) and portal (<Code>/portal/*</Code>) look. Left-panel gradient + right-panel background + accent button.</Hint>
          <Grid2>
            <ColorField label="Gradient — from (top-left)" path="customer_theme.gradient_from" draft={draft} set={set} />
            <ColorField label="Gradient — to (bottom-right)" path="customer_theme.gradient_to" draft={draft} set={set} />
            <ColorField label="Accent (button + link)" path="customer_theme.accent" draft={draft} set={set} />
            <ColorField label="Card background" path="customer_theme.card_bg" draft={draft} set={set} />
            <ColorField label="Right panel background" path="customer_theme.panel_bg" draft={draft} set={set} />
            <ColorField label="Text on gradient" path="customer_theme.text_on_gradient" draft={draft} set={set} />
          </Grid2>
          <div>
            <SubTitle>Quick presets</SubTitle>
            <div className="flex flex-wrap gap-2">
              <ThemePreset label="Sunset orange (default)" onClick={() => set("customer_theme", { gradient_from:"#EA580C", gradient_to:"#F59E0B", accent:"#F97316", card_bg:"#FFFFFF", panel_bg:"#FFF9F5", text_on_gradient:"#FFFFFF" })} />
              <ThemePreset label="Cool teal" onClick={() => set("customer_theme", { gradient_from:"#0F766E", gradient_to:"#2DD4BF", accent:"#14B8A6", card_bg:"#FFFFFF", panel_bg:"#F0FDFA", text_on_gradient:"#FFFFFF" })} />
              <ThemePreset label="Royal indigo" onClick={() => set("customer_theme", { gradient_from:"#4338CA", gradient_to:"#8B5CF6", accent:"#6366F1", card_bg:"#FFFFFF", panel_bg:"#F5F3FF", text_on_gradient:"#FFFFFF" })} />
              <ThemePreset label="Emerald fresh" onClick={() => set("customer_theme", { gradient_from:"#059669", gradient_to:"#84CC16", accent:"#10B981", card_bg:"#FFFFFF", panel_bg:"#F0FDF4", text_on_gradient:"#FFFFFF" })} />
              <ThemePreset label="Rose gold" onClick={() => set("customer_theme", { gradient_from:"#DB2777", gradient_to:"#F97316", accent:"#EC4899", card_bg:"#FFFFFF", panel_bg:"#FFF1F2", text_on_gradient:"#FFFFFF" })} />
            </div>
          </div>
          <LoginThemePreview kind="customer" theme={draft.customer_theme || {}} />
        </>
      );

    case "theme-staff":
      return (
        <>
          <Hint>Controls the staff sign-in look. Dark theme with two accent glows.</Hint>
          <Grid2>
            <ColorField label="Background (main)" path="staff_theme.bg" draft={draft} set={set} />
            <ColorField label="Card background" path="staff_theme.card_bg" draft={draft} set={set} />
            <ColorField label="Accent (primary)" path="staff_theme.accent" draft={draft} set={set} />
            <ColorField label="Accent (secondary)" path="staff_theme.accent_secondary" draft={draft} set={set} />
            <ColorField label="Base text" path="staff_theme.text" draft={draft} set={set} />
            <ColorField label="Muted text" path="staff_theme.muted_text" draft={draft} set={set} />
            <ColorField label="Background orb #1 (glow)" path="staff_theme.orb_1" draft={draft} set={set} />
            <ColorField label="Background orb #2 (glow)" path="staff_theme.orb_2" draft={draft} set={set} />
          </Grid2>
          <div>
            <SubTitle>Quick presets</SubTitle>
            <div className="flex flex-wrap gap-2">
              <ThemePreset label="Slate + orange (default)" onClick={() => set("staff_theme", { bg:"#020617", card_bg:"#0F172A", accent:"#F97316", accent_secondary:"#38BDF8", text:"#F1F5F9", muted_text:"#94A3B8", orb_1:"#F97316", orb_2:"#0EA5E9" })} />
              <ThemePreset label="Midnight + violet" onClick={() => set("staff_theme", { bg:"#0F0A24", card_bg:"#1A1240", accent:"#A855F7", accent_secondary:"#EC4899", text:"#F5F3FF", muted_text:"#A78BFA", orb_1:"#A855F7", orb_2:"#EC4899" })} />
              <ThemePreset label="Carbon + neon" onClick={() => set("staff_theme", { bg:"#0A0A0A", card_bg:"#141414", accent:"#22D3EE", accent_secondary:"#84CC16", text:"#FAFAFA", muted_text:"#71717A", orb_1:"#22D3EE", orb_2:"#84CC16" })} />
              <ThemePreset label="Forest + amber" onClick={() => set("staff_theme", { bg:"#052E1E", card_bg:"#0A3D2A", accent:"#FBBF24", accent_secondary:"#34D399", text:"#ECFDF5", muted_text:"#A7F3D0", orb_1:"#FBBF24", orb_2:"#10B981" })} />
              <ThemePreset label="Cool teal" onClick={() => set("staff_theme", { bg:"#042F2E", card_bg:"#0A4A45", accent:"#2DD4BF", accent_secondary:"#67E8F9", text:"#ECFEFF", muted_text:"#99F6E4", orb_1:"#14B8A6", orb_2:"#22D3EE" })} />
            </div>
          </div>
          <LoginThemePreview kind="staff" theme={draft.staff_theme || {}} />
        </>
      );

    /* ═════ CONTENT TAB ═════ */
    case "hero":
      return (
        <Grid2>
          <Field label="Overline (small caps above headline)"><Input value={draft.hero?.overline || ""} onChange={(e) => set("hero.overline", e.target.value)} /></Field>
          <div />
          <FieldFull label="Headline (use newline for accent line 2)"><Textarea rows={2} value={draft.hero?.headline || ""} onChange={(e) => set("hero.headline", e.target.value)} data-testid="cms-hero-headline-input" /></FieldFull>
          <FieldFull label="Subheadline"><Textarea rows={2} value={draft.hero?.subheadline || ""} onChange={(e) => set("hero.subheadline", e.target.value)} /></FieldFull>
          <Field label="CTA primary label"><Input value={draft.hero?.cta_primary_label || ""} onChange={(e) => set("hero.cta_primary_label", e.target.value)} /></Field>
          <Field label="CTA primary link"><Input value={draft.hero?.cta_primary_href || ""} onChange={(e) => set("hero.cta_primary_href", e.target.value)} /></Field>
          <Field label="CTA secondary label"><Input value={draft.hero?.cta_secondary_label || ""} onChange={(e) => set("hero.cta_secondary_label", e.target.value)} /></Field>
          <Field label="CTA secondary link"><Input value={draft.hero?.cta_secondary_href || ""} onChange={(e) => set("hero.cta_secondary_href", e.target.value)} /></Field>
        </Grid2>
      );

    case "hero-background":
      return (
        <>
          <Hint>Choose how the hero card looks. Upload photos or videos directly — files auto-resize via <Code>object-cover</Code>. Multiple image upload auto-creates a slideshow.</Hint>
          <FieldFull label="Background mode">
            <div className="flex flex-wrap gap-2" data-testid="cms-hero-mode-picker">
              {[
                { v: "gradient", label: "Solid gradient", hint: "Simple primary → navy fade" },
                { v: "image", label: "Single image", hint: "Upload one photo" },
                { v: "slideshow", label: "Photo slideshow", hint: "Upload multiple → auto rotate" },
                { v: "video", label: "Video loop", hint: "Autoplay muted background video" },
                { v: "animated", label: "Animated CSS", hint: "Pick a motion preset" },
              ].map((opt) => {
                const active = (draft.hero?.background_mode || "gradient") === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => set("hero.background_mode", opt.v)}
                    className={`flex-1 min-w-[150px] rounded-lg border p-3 text-left transition-all ${active ? "border-primary bg-primary/10 shadow" : "border-border hover:border-primary/40"}`}
                    data-testid={`cms-hero-mode-${opt.v}`}
                  >
                    <div className="text-sm font-bold">{opt.label}</div>
                    <div className="text-[10px] text-muted-foreground">{opt.hint}</div>
                  </button>
                );
              })}
            </div>
          </FieldFull>

          {draft.hero?.background_mode === "image" && (
            <FieldFull label="Hero image (uploads served from /api/cms/media/…)">
              <MediaUploader mode="single-image" value={draft.hero?.background_image_url || ""} onChange={(v) => set("hero.background_image_url", v)} testIdPrefix="cms-hero-image" />
            </FieldFull>
          )}

          {draft.hero?.background_mode === "video" && (
            <FieldFull label="Hero video (mp4 / webm — autoplayed muted + looped)">
              <MediaUploader mode="single-video" value={draft.hero?.background_video_url || ""} onChange={(v) => set("hero.background_video_url", v)} testIdPrefix="cms-hero-video" />
            </FieldFull>
          )}

          {draft.hero?.background_mode === "animated" && (
            <FieldFull label="Animation preset">
              <div className="grid grid-cols-2 gap-2 md:grid-cols-3" data-testid="cms-hero-anim-picker">
                {[
                  { v: "aurora", label: "Aurora blobs", desc: "Soft flowing color blobs" },
                  { v: "grid", label: "Grid pulse", desc: "Tech-mesh scanline" },
                  { v: "highway", label: "Highway lane", desc: "Perspective road speeding" },
                  { v: "waves", label: "Wave drift", desc: "Layered SVG waves" },
                  { v: "sparks", label: "Rising sparks", desc: "Floating particles" },
                  { v: "orbit", label: "Orbit rings", desc: "Rotating concentric rings" },
                ].map((opt) => {
                  const active = (draft.hero?.animation_preset || "aurora") === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => set("hero.animation_preset", opt.v)}
                      className={`rounded-lg border p-3 text-left transition-all ${active ? "border-primary bg-primary/10 shadow" : "border-border hover:border-primary/40"}`}
                      data-testid={`cms-hero-anim-${opt.v}`}
                    >
                      <div className="text-sm font-bold">{opt.label}</div>
                      <div className="text-[10px] text-muted-foreground">{opt.desc}</div>
                    </button>
                  );
                })}
              </div>
            </FieldFull>
          )}

          {draft.hero?.background_mode === "slideshow" && (
            <Grid2>
              <Field label="Slideshow interval (ms)">
                <Input
                  type="number" min={1500} step={500}
                  value={draft.hero?.slideshow_interval_ms ?? 4500}
                  onChange={(e) => set("hero.slideshow_interval_ms", Number(e.target.value) || 4500)}
                  data-testid="cms-hero-slide-interval"
                />
                <div className="mt-1 text-[10px] text-muted-foreground">Per-slide duration. Minimum 1500ms.</div>
              </Field>
              <div />
              <FieldFull label="Slideshow images (upload many at once)">
                <MediaUploader mode="multi-image" value={draft.hero?.slideshow_images || []} onChange={(v) => set("hero.slideshow_images", v)} testIdPrefix="cms-hero-slides" />
              </FieldFull>
            </Grid2>
          )}
        </>
      );

    case "about":
      return (
        <>
          <Grid2>
            <Field label="Eyebrow"><Input value={draft.about?.eyebrow || ""} onChange={(e) => set("about.eyebrow", e.target.value)} /></Field>
            <Field label="Headline"><Input value={draft.about?.headline || ""} onChange={(e) => set("about.headline", e.target.value)} /></Field>
            <FieldFull label="Body copy (long)"><Textarea rows={4} value={draft.about?.body || ""} onChange={(e) => set("about.body", e.target.value)} /></FieldFull>
            <FieldFull label="Descriptive paragraph"><Textarea rows={3} value={draft.about?.description || ""} onChange={(e) => set("about.description", e.target.value)} /></FieldFull>
            <FieldFull label="Brand statement (chip)"><Input value={draft.about?.brand_statement || ""} onChange={(e) => set("about.brand_statement", e.target.value)} placeholder="FuelExpress — Fuel Delivered. Business Powered." /></FieldFull>
          </Grid2>

          <div className="mt-6 rounded-lg border border-border/60 bg-muted/20 p-4">
            <div className="mb-3 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Mission &amp; Vision</div>
            <Grid2>
              <Field label="Short mission (one-liner)">
                <Input value={draft.about?.short_mission || ""} onChange={(e) => set("about.short_mission", e.target.value)} placeholder="Delivering the right fuel, in the right quantity, at the right time." />
              </Field>
              <Field label="Short vision (one-liner)">
                <Input value={draft.about?.short_vision || ""} onChange={(e) => set("about.short_vision", e.target.value)} placeholder="To power businesses everywhere with smart, safe and reliable fuel delivery." />
              </Field>
              <FieldFull label="Mission (long)">
                <Textarea rows={3} value={draft.about?.mission || ""} onChange={(e) => set("about.mission", e.target.value)} />
              </FieldFull>
              <FieldFull label="Vision (long)">
                <Textarea rows={3} value={draft.about?.vision || ""} onChange={(e) => set("about.vision", e.target.value)} />
              </FieldFull>
            </Grid2>
          </div>

          <div className="mt-6">
            <div className="mb-3 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Core values</div>
            <ArrayEditor items={draft.about?.core_values || []} onChange={(v) => set("about.core_values", v)}
              columns={[
                { key: "icon", label: "Icon (ShieldCheck, Gauge, HardHat, Clock, Eye, Cpu, Heart, Sparkles)" },
                { key: "title", label: "Title" },
                { key: "description", label: "Description", textarea: true },
              ]}
              addLabel="Add core value"
              newRow={() => ({ icon: "Sparkles", title: "New value", description: "" })}
            />
          </div>
        </>
      );

    case "services":
      return (
        <ArrayEditor items={draft.services || []} onChange={(v) => setArr("services", v)}
          columns={[
            { key: "icon", label: "Icon (Truck, Package, Zap, Calendar, Building2, Factory, Fuel)" },
            { key: "title", label: "Title" },
            { key: "description", label: "Description", textarea: true },
            { key: "image_url", label: "Image URL (card cover)" },
          ]}
          addLabel="Add service"
          newRow={() => ({ icon: "Fuel", title: "New service", description: "", image_url: "" })}
        />
      );

    case "industries":
      return (
        <FieldFull label="Industries (comma-separated)">
          <Textarea rows={3} value={(draft.industries || []).join(", ")} onChange={(e) => set("industries", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
        </FieldFull>
      );

    case "commitment":
      return (
        <FieldFull label="Commitment items (one per line)">
          <Textarea rows={5} value={(draft.commitment || []).join("\n")} onChange={(e) => set("commitment", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} />
        </FieldFull>
      );

    case "safety":
      return (
        <Grid2>
          <Field label="Headline"><Input value={draft.safety?.headline || ""} onChange={(e) => set("safety.headline", e.target.value)} /></Field>
          <Field label="Subtitle"><Input value={draft.safety?.subtitle || ""} onChange={(e) => set("safety.subtitle", e.target.value)} /></Field>
          <FieldFull label="Procedure items (one per line)"><Textarea rows={4} value={(draft.safety?.items || []).join("\n")} onChange={(e) => set("safety.items", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} /></FieldFull>
          <FieldFull label="Footnote"><Textarea rows={2} value={draft.safety?.footnote || ""} onChange={(e) => set("safety.footnote", e.target.value)} /></FieldFull>
        </Grid2>
      );

    case "benefits":
      return (
        <FieldFull label="Benefit items (one per line)">
          <Textarea rows={5} value={(draft.benefits || []).join("\n")} onChange={(e) => set("benefits", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} />
        </FieldFull>
      );

    case "metrics":
      return (
        <ArrayEditor items={draft.trust_metrics || []} onChange={(v) => setArr("trust_metrics", v)}
          columns={[{ key: "value", label: "Value" }, { key: "label", label: "Label" }]}
          addLabel="Add metric"
          newRow={() => ({ value: "0", label: "New metric" })}
        />
      );

    case "live-stats":
      return <LiveWidgetSection />;

    case "features":
      return (
        <ArrayEditor items={draft.features || []} onChange={(v) => setArr("features", v)}
          columns={[
            { key: "icon", label: "Icon (Fuel, ShieldCheck, MapPin, FileText, MessageSquare, IndianRupee, Clock, Truck)" },
            { key: "title", label: "Title" },
            { key: "description", label: "Description", textarea: true },
          ]}
          addLabel="Add feature"
          newRow={() => ({ icon: "Fuel", title: "New feature", description: "" })}
        />
      );

    case "how":
      return (
        <ArrayEditor items={draft.how_it_works || []} onChange={(v) => setArr("how_it_works", v)}
          columns={[
            { key: "step", label: "#", size: "sm" },
            { key: "title", label: "Title" },
            { key: "description", label: "Description", textarea: true },
          ]}
          addLabel="Add step"
          newRow={() => ({ step: (draft.how_it_works?.length || 0) + 1, title: "New step", description: "" })}
        />
      );

    case "coverage":
      return (
        <Grid2>
          <Field label="Section title"><Input value={draft.coverage?.title || ""} onChange={(e) => set("coverage.title", e.target.value)} /></Field>
          <Field label="Map image URL"><Input value={draft.coverage?.map_image_url || ""} onChange={(e) => set("coverage.map_image_url", e.target.value)} /></Field>
          <FieldFull label="Subtitle"><Textarea rows={2} value={draft.coverage?.subtitle || ""} onChange={(e) => set("coverage.subtitle", e.target.value)} /></FieldFull>
          <FieldFull label="Cities (comma-separated)"><Textarea rows={2} value={(draft.coverage?.cities || []).join(", ")} onChange={(e) => set("coverage.cities", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} /></FieldFull>
        </Grid2>
      );

    case "testimonials":
      return (
        <ArrayEditor items={draft.testimonials || []} onChange={(v) => setArr("testimonials", v)}
          columns={[
            { key: "name", label: "Name" },
            { key: "role", label: "Role / Company" },
            { key: "quote", label: "Quote", textarea: true },
            { key: "image_url", label: "Photo URL" },
          ]}
          addLabel="Add testimonial"
          newRow={() => ({ name: "", role: "", quote: "", image_url: "" })}
        />
      );

    case "faq":
      return (
        <ArrayEditor items={draft.faq || []} onChange={(v) => setArr("faq", v)}
          columns={[
            { key: "question", label: "Question" },
            { key: "answer", label: "Answer", textarea: true },
          ]}
          addLabel="Add FAQ"
          newRow={() => ({ question: "", answer: "" })}
        />
      );

    case "app-download": {
      const enabled = draft.app_download?.enabled !== false;
      return (
        <>
          <div
            className={`mb-4 flex items-center justify-between gap-3 rounded-lg border p-3 transition-colors ${enabled ? "border-emerald-500/40 bg-emerald-500/5" : "border-amber-500/40 bg-amber-500/5"}`}
            data-testid="cms-app-download-toggle"
          >
            <div className="min-w-0">
              <div className="text-sm font-bold">
                App download banner is <span className={enabled ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}>{enabled ? "visible" : "hidden"}</span> on the landing page
              </div>
              <div className="text-[11px] text-muted-foreground">
                {enabled
                  ? "The \"Upgrade your refueling experience\" section shows on the home page with your iOS + Android CTAs."
                  : "The section is completely removed from the public landing page. Switch on when your app is published to the stores."}
              </div>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={(v) => set("app_download.enabled", v)}
              data-testid="cms-app-download-switch"
              aria-label="Enable app download banner"
            />
          </div>
          <Grid2>
            <Field label="Mockup image URL"><Input value={draft.app_download?.mockup_image_url || ""} onChange={(e) => set("app_download.mockup_image_url", e.target.value)} disabled={!enabled} /></Field>
            <Field label="iOS App Store URL"><Input value={draft.app_download?.ios_url || ""} onChange={(e) => set("app_download.ios_url", e.target.value)} disabled={!enabled} placeholder="https://apps.apple.com/…" /></Field>
            <FieldFull label="Headline"><Input value={draft.app_download?.headline || ""} onChange={(e) => set("app_download.headline", e.target.value)} disabled={!enabled} /></FieldFull>
            <FieldFull label="Subheadline"><Textarea rows={2} value={draft.app_download?.subheadline || ""} onChange={(e) => set("app_download.subheadline", e.target.value)} disabled={!enabled} /></FieldFull>
            <Field label="Google Play URL"><Input value={draft.app_download?.android_url || ""} onChange={(e) => set("app_download.android_url", e.target.value)} disabled={!enabled} placeholder="https://play.google.com/…" /></Field>
          </Grid2>
        </>
      );
    }

    case "contact":
      return (
        <Grid2>
          <Field label="Email"><Input value={draft.contact?.email || ""} onChange={(e) => set("contact.email", e.target.value)} /></Field>
          <Field label="Phone"><Input value={draft.contact?.phone || ""} onChange={(e) => set("contact.phone", e.target.value)} /></Field>
          <Field label="WhatsApp"><Input value={draft.contact?.whatsapp || ""} onChange={(e) => set("contact.whatsapp", e.target.value)} /></Field>
          <FieldFull label="Address"><Textarea rows={2} value={draft.contact?.address || ""} onChange={(e) => set("contact.address", e.target.value)} /></FieldFull>
        </Grid2>
      );

    case "support_contact":
      return (
        <Grid2>
          <Field label="Support email">
            <Input
              value={draft.support_contact?.email || ""}
              onChange={(e) => set("support_contact.email", e.target.value)}
              placeholder="support@yourcompany.com"
              data-testid="cms-support-email"
            />
          </Field>
          <Field label="Support phone (call)">
            <Input
              value={draft.support_contact?.phone || ""}
              onChange={(e) => set("support_contact.phone", e.target.value)}
              placeholder="+91 98765 43210"
              data-testid="cms-support-phone"
            />
          </Field>
          <Field label="Support WhatsApp">
            <Input
              value={draft.support_contact?.whatsapp || ""}
              onChange={(e) => set("support_contact.whatsapp", e.target.value)}
              placeholder="+91 98765 43210"
              data-testid="cms-support-whatsapp"
            />
          </Field>
          <Field label="Support hours (optional)">
            <Input
              value={draft.support_contact?.hours || ""}
              onChange={(e) => set("support_contact.hours", e.target.value)}
              placeholder="Mon–Sat · 9 AM to 8 PM"
              data-testid="cms-support-hours"
            />
          </Field>
        </Grid2>
      );

    case "social": {
      const platforms = [
        { key: "facebook",  label: "Facebook",    Icon: Facebook,      placeholder: "https://facebook.com/your-page" },
        { key: "twitter",   label: "X (Twitter)", Icon: Twitter,       placeholder: "https://x.com/your-handle" },
        { key: "instagram", label: "Instagram",   Icon: Instagram,     placeholder: "https://instagram.com/your-handle" },
        { key: "linkedin",  label: "LinkedIn",    Icon: Linkedin,      placeholder: "https://linkedin.com/company/your-company" },
        { key: "youtube",   label: "YouTube",     Icon: Youtube,       placeholder: "https://youtube.com/@your-channel" },
        { key: "whatsapp",  label: "WhatsApp",    Icon: MessageSquare, placeholder: "https://wa.me/919999999999" },
        { key: "telegram",  label: "Telegram",    Icon: Send,          placeholder: "https://t.me/your-channel" },
      ];
      const rootEnabled = draft.social_media?.enabled !== false;
      const activeCount = platforms.filter((p) => {
        const cfg = draft.social_media?.[p.key];
        return cfg?.enabled && (cfg.url || "").trim();
      }).length;
      return (
        <>
          <Hint>Icons appear in the public landing footer (below the tagline). Toggle each platform on and paste the full profile URL. Empty URLs are silently hidden even when enabled.</Hint>

          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border/60 p-3 hover:bg-muted/30">
            <input
              type="checkbox"
              checked={rootEnabled}
              onChange={(e) => set("social_media.enabled", e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-primary"
              data-testid="social-master-toggle"
            />
            <div className="flex-1">
              <div className="text-sm font-semibold">Show social icons in landing footer</div>
              <div className="text-[11px] text-muted-foreground">
                Master switch. When off, no icons are rendered regardless of per-platform toggles. Currently <span className="font-mono">{activeCount}</span> platform(s) live.
              </div>
            </div>
          </label>

          <div className="space-y-2">
            {platforms.map(({ key, label, Icon, placeholder }) => {
              const cfg = draft.social_media?.[key] || {};
              const enabled = !!cfg.enabled;
              return (
                <div key={key} className={`grid grid-cols-[auto_auto_1fr] items-center gap-3 rounded-lg border p-3 transition-colors ${enabled ? "border-primary/40 bg-primary/5" : "border-border/60"}`}>
                  <div className="grid h-9 w-9 place-items-center rounded-md bg-background text-foreground/70">
                    <Icon size={16} />
                  </div>
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => set(`social_media.${key}.enabled`, e.target.checked)}
                      className="h-4 w-4 accent-primary"
                      data-testid={`social-${key}-toggle`}
                    />
                    <span className="w-24 text-sm font-semibold">{label}</span>
                  </label>
                  <Input
                    value={cfg.url || ""}
                    onChange={(e) => set(`social_media.${key}.url`, e.target.value)}
                    placeholder={placeholder}
                    disabled={!enabled}
                    className="font-mono text-xs"
                    data-testid={`social-${key}-url`}
                  />
                </div>
              );
            })}
          </div>
        </>
      );
    }

    case "footer":
      return (
        <Grid2>
          <FieldFull label="Tagline"><Input value={draft.footer?.tagline || ""} onChange={(e) => set("footer.tagline", e.target.value)} /></FieldFull>
          <FieldFull label="Copyright text"><Input value={draft.footer?.copyright || ""} onChange={(e) => set("footer.copyright", e.target.value)} /></FieldFull>
        </Grid2>
      );

    /* ═════ SEO TAB ═════ */
    case "seo":
      return <SEOFields draft={draft} set={set} />;

    /* ═════ LEGAL & ACCESS TAB ═════ */
    case "legal":
      return (
        <>
          <Hint>Published at <Code>/privacy</Code> and <Code>/terms</Code>. Supports light Markdown — <Code>#</Code> headings, <Code>-</Code> bullets, <Code>**bold**</Code>, <Code>[link](url)</Code>.</Hint>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Privacy Policy</Label>
              <Link to="/privacy" target="_blank" className="text-xs text-primary hover:underline">Open live page <ExternalLink size={10} className="inline" /></Link>
            </div>
            <Textarea
              rows={14}
              value={draft.legal?.privacy_policy || ""}
              onChange={(e) => set("legal.privacy_policy", e.target.value)}
              placeholder="# Privacy Policy&#10;&#10;## 1. Information we collect …"
              className="font-mono text-xs"
              data-testid="cms-privacy-policy"
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Terms &amp; Conditions</Label>
              <Link to="/terms" target="_blank" className="text-xs text-primary hover:underline">Open live page <ExternalLink size={10} className="inline" /></Link>
            </div>
            <Textarea
              rows={14}
              value={draft.legal?.terms || ""}
              onChange={(e) => set("legal.terms", e.target.value)}
              placeholder="# Terms and Conditions&#10;&#10;## 1. Services …"
              className="font-mono text-xs"
              data-testid="cms-terms"
            />
          </div>
        </>
      );

    case "access-customer":
      return (
        <div className="space-y-3">
          {/* Site-wide kill switch — blocks public site + customer login */}
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border/60 p-4 hover:bg-muted/30">
            <input
              type="checkbox"
              checked={draft.brand?.site_online !== false}
              onChange={(e) => set("brand.site_online", e.target.checked)}
              className="mt-1 h-4 w-4 accent-primary"
              data-testid="site-online-toggle"
            />
            <div className="flex-1">
              <div className="text-sm font-semibold">Site is online</div>
              <div className="mt-1 text-xs text-muted-foreground">
                When <b>off</b>, the entire public site — landing page, <Code>/login</Code>, <Code>/register</Code> — shows a maintenance page.
                Admins remain able to sign in via the hidden staff URL <Code>/ops-console-fx-9k2m4</Code> so you can flip this back on.
              </div>
              {draft.brand?.site_online === false && (
                <div className="mt-2">
                  <Label>Maintenance message shown to visitors</Label>
                  <Textarea
                    rows={2}
                    value={draft.brand?.maintenance_message || ""}
                    onChange={(e) => set("brand.maintenance_message", e.target.value)}
                    data-testid="maintenance-message"
                    placeholder="We're upgrading systems and will be back online shortly."
                  />
                </div>
              )}
            </div>
          </label>

          {/* Customer-login-only kill switch */}
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border/60 p-4 hover:bg-muted/30">
            <input
              type="checkbox"
              checked={draft.brand?.customer_login_enabled !== false}
              onChange={(e) => set("brand.customer_login_enabled", e.target.checked)}
              className="mt-1 h-4 w-4 accent-primary"
              data-testid="customer-login-toggle"
            />
            <div className="flex-1">
              <div className="text-sm font-semibold">Enable customer sign-in at <Code>/login</Code></div>
              <div className="mt-1 text-xs text-muted-foreground">
                When disabled, the customer login page shows a maintenance banner and blocks sign-in.
                Active customer sessions are force-logged out on their next request. Staff sign-in is unaffected.
              </div>
            </div>
          </label>

          {/* Auto-activate customers after OTP verification */}
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border/60 p-4 hover:bg-muted/30">
            <input
              type="checkbox"
              checked={draft.brand?.registration_auto_activate !== false}
              onChange={(e) => set("brand.registration_auto_activate", e.target.checked)}
              className="mt-1 h-4 w-4 accent-primary"
              data-testid="registration-auto-activate-toggle"
            />
            <div className="flex-1">
              <div className="text-sm font-semibold">Auto-activate customers after OTP verification</div>
              <div className="mt-1 text-xs text-muted-foreground">
                When <b>on</b> (default), a customer who verifies their email/SMS OTP is immediately signed in and can place orders.
                When <b>off</b>, the customer&apos;s account stays inactive after OTP verification — an admin must approve them from <Code>Admin → Users</Code>.
              </div>
            </div>
          </label>

          {/* PWA install prompt banner — master + per-panel */}
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border/60 p-4 hover:bg-muted/30">
            <input
              type="checkbox"
              checked={draft.brand?.pwa_install_prompt_enabled !== false}
              onChange={(e) => set("brand.pwa_install_prompt_enabled", e.target.checked)}
              className="mt-1 h-4 w-4 accent-primary"
              data-testid="pwa-install-prompt-toggle"
            />
            <div className="flex-1">
              <div className="text-sm font-semibold">Show &quot;Install FuelExpress&quot; PWA banner (master switch)</div>
              <div className="mt-1 text-xs text-muted-foreground">
                When <b>off</b>, the install banner is hidden everywhere. When <b>on</b>, individual panels can still be turned off below.
              </div>
              <div className={`mt-3 grid gap-2 ${draft.brand?.pwa_install_prompt_enabled === false ? "opacity-50 pointer-events-none" : ""}`}>
                <label className="flex cursor-pointer items-center gap-3 rounded-md border border-border/40 bg-background p-2.5">
                  <input
                    type="checkbox"
                    checked={draft.brand?.pwa_install_prompt_customer !== false}
                    onChange={(e) => set("brand.pwa_install_prompt_customer", e.target.checked)}
                    className="h-4 w-4 accent-primary"
                    data-testid="pwa-install-prompt-customer-toggle"
                  />
                  <div className="text-xs">
                    <span className="font-semibold">Customer portal</span> — <span className="text-muted-foreground">shown on <Code>/portal/*</Code></span>
                  </div>
                </label>
                <label className="flex cursor-pointer items-center gap-3 rounded-md border border-border/40 bg-background p-2.5">
                  <input
                    type="checkbox"
                    checked={draft.brand?.pwa_install_prompt_driver !== false}
                    onChange={(e) => set("brand.pwa_install_prompt_driver", e.target.checked)}
                    className="h-4 w-4 accent-primary"
                    data-testid="pwa-install-prompt-driver-toggle"
                  />
                  <div className="text-xs">
                    <span className="font-semibold">Driver app</span> — <span className="text-muted-foreground">shown on <Code>/driver/*</Code></span>
                  </div>
                </label>
                <label className="flex cursor-pointer items-center gap-3 rounded-md border border-border/40 bg-background p-2.5">
                  <input
                    type="checkbox"
                    checked={draft.brand?.pwa_install_prompt_auth !== false}
                    onChange={(e) => set("brand.pwa_install_prompt_auth", e.target.checked)}
                    className="h-4 w-4 accent-primary"
                    data-testid="pwa-install-prompt-auth-toggle"
                  />
                  <div className="text-xs">
                    <span className="font-semibold">Auth pages</span> — <span className="text-muted-foreground">shown on <Code>/login</Code> + <Code>/register</Code></span>
                  </div>
                </label>
              </div>
            </div>
          </label>
        </div>
      );

    case "danger-zone":
      return <DangerZoneCard />;

    default:
      return <div className="text-sm text-muted-foreground">Section not found: {id}</div>;
  }
}
